import { useCallback, useEffect, useMemo, useState } from 'react'
import { X, Loader2, Banknote, AlertTriangle, FileDown, Download, Mail } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { buildExpenseTransferFiles, money, ISSUE_LABEL, EXPENSE_REMARK_MAX } from '../../lib/scbBulk.js'
import { zipText, downloadBlob } from '../../lib/zip.js'
import { fmtMoney } from '../../lib/expense.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

const todayISO = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * ไฟล์โอนเงิน SCB (ค่าใช้จ่าย) — the SCB Business Net bulk-transfer files for the
 * expenses currently on screen that are still รอจ่าย, zipped.
 *
 * The split is Branch.Company × ไทยพาณิชย์-or-not: up to five companies × two
 * products = ten files. Several branches share one company (สนามเป้า, อ่อนนุช,
 * อารีย์, Tooth Taxi and บางกะปิ are all บริษัท สไมล์ซีซันส์), so their expenses
 * merge into that company's file and debit its own SCB account. ไทยพาณิชย์ → 3PT,
 * everything else → RFT.
 *
 * ONE TRANSFER LINE PER EXPENSE, never merged per vendor: three unpaid invoices
 * from the same landlord leave three debits on the statement, each matching one
 * row in ค่าใช้จ่าย. Reconciling afterwards is the whole point.
 *
 * SCOPE is the page's own filters (ปี / เดือน / สาขา / บริษัท / ค้นหา) narrowed to
 * รอจ่าย — the caller hands the ids down. That matters: `paid` is not maintained
 * across old records (2,274 rows are still รอจ่าย back to 2022), so "everything
 * unpaid" is never the right set. Xano RE-READS `paid` for every id, so a record
 * ticked จ่ายแล้ว since the page loaded comes back as skipped, not as a transfer.
 *
 * Downloading does NOT mark anything จ่ายแล้ว — a saved file is not a payment,
 * and Business Net can still reject the upload.
 */
export default function ExpenseScbModal({ expenseIds, periodLabel, scopeLabel, onClose }) {
  const [valueDate, setValueDate] = useState(todayISO)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const today = todayISO()
  const pastDate = valueDate < today

  // The id list is stable for the life of the dialog (the page's filtered set is
  // frozen when it opens), so join it into a key rather than depending on the
  // array identity — the parent rebuilds that array on every render.
  const idsKey = useMemo(() => (expenseIds || []).join(','), [expenseIds])

  useEffect(() => {
    let alive = true
    setLoading(true); setErr('')
    const ids = idsKey ? idsKey.split(',').map(Number) : []
    if (!ids.length) { setRows([]); setLoading(false); return }
    postJson('/api/expense/scb-bulk', { expense_ids: ids })
      .then((res) => { if (alive) setRows(Array.isArray(res?.rows) ? res.rows : []) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [idsKey])

  const build = useCallback(
    () => buildExpenseTransferFiles({ rows, periodLabel, valueDate }),
    [rows, periodLabel, valueDate],
  )
  // Preview only — refs are regenerated at download so every submitted file
  // carries fresh transaction references.
  const preview = useMemo(build, [build])

  function download() {
    if (pastDate || !preview.files.length) return
    setBusy(true)
    try {
      const built = build()
      const blob = zipText(built.files.map((f) => ({ name: f.name, text: f.text })))
      downloadBlob(blob, `SCB_expense_${periodLabel || 'all'}_${valueDate}.zip`)
      onClose()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  const totalIds = idsKey ? idsKey.split(',').length : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[620px] bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-ss-pop-in max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <Banknote size={17} className="text-brand-600" />
            <h2 className="text-[15.5px] font-semibold text-slate-900">ไฟล์โอนเงิน SCB</h2>
            <span className="text-[12px] text-slate-400">ค่าใช้จ่ายที่รอจ่าย</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <div className="mb-4">
            <label className={LABEL}>วันที่ต้องการโอนเงิน (Value Date)</label>
            <input
              type="date" value={valueDate} min={today}
              onChange={(e) => setValueDate(e.target.value)}
              className={`${FIELD} ${pastDate ? 'border-rose-300' : ''}`}
            />
            {pastDate
              ? <p className="text-[12px] text-rose-500 mt-1">วันที่โอนต้องไม่ย้อนหลัง — ธนาคารจะปฏิเสธไฟล์</p>
              : <p className="text-[12px] text-slate-400 mt-1">
                  {scopeLabel} · รอจ่าย {totalIds.toLocaleString()} รายการ
                </p>}
          </div>

          {/* Not a choice: the advice email always goes out (see
              buildExpenseTransferFiles). Stated anyway, because it is mail sent
              to an outside party in the clinic's name and the operator should
              know it leaves. */}
          <div className="mb-4 flex items-start gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5">
            <Mail size={14} className="mt-0.5 text-slate-400 shrink-0" />
            <span className="text-[12.5px] leading-snug">
              <span className="font-medium text-slate-700">ธนาคารจะส่งอีเมลแจ้งการโอนถึงผู้ขาย</span>
              <span className="block text-slate-400 mt-0.5">
                SCB ส่ง payment advice ไปยังอีเมลในข้อมูลบริษัทผู้ขาย — ผู้ขายที่ยังไม่มีอีเมลในระบบจะไม่ได้รับ
              </span>
            </span>
          </div>

          {loading && (
            <div className="py-8 text-center text-slate-400">
              <Loader2 size={22} className="animate-spin mx-auto mb-2" /> กำลังดึงรายการ…
            </div>
          )}

          {err && <div className="mb-3 text-[13px] text-rose-500">{err}</div>}

          {!loading && !err && (
            <>
              {preview.skipped.length > 0 && (
                <div className="mb-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-800 mb-1.5">
                    <AlertTriangle size={14} /> ข้ามไป {preview.skipped.length} รายการ
                  </div>
                  <ul className="text-[12.5px] text-amber-800/90 space-y-0.5 max-h-40 overflow-y-auto">
                    {preview.skipped.map((s) => (
                      <li key={s.expense_id} className="truncate">
                        <span className="text-amber-900/60 tabular-nums">#{s.expense_id}</span>{' '}
                        {s.name || s.detail || '—'} — {ISSUE_LABEL[s.issue] || s.issue}
                        {s.issue === 'net_not_positive' ? ` (${fmtMoney(Number(s.net))})` : ''}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11.5px] text-amber-700/80 mt-1.5">
                    เติมเลขบัญชีในข้อมูลบริษัทผู้ขาย แล้วสร้างไฟล์ใหม่อีกครั้ง
                  </p>
                </div>
              )}

              {preview.files.length === 0 ? (
                <div className="py-6 text-center text-[13px] text-slate-400">
                  ไม่มีรายการที่โอนได้ในชุดนี้
                </div>
              ) : (
                <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-50 text-slate-400">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">ไฟล์</th>
                        <th className="text-left font-medium px-3 py-2">บัญชีตัดเงิน</th>
                        <th className="text-right font-medium px-3 py-2">รายการ</th>
                        <th className="text-right font-medium px-3 py-2">ยอดโอน</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.files.map((f) => (
                        <tr key={f.name}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <FileDown size={13} className="text-slate-300 shrink-0" />
                              <span className="truncate">{f.name}</span>
                              <span className="text-[10.5px] font-medium text-slate-400 ring-1 ring-slate-200 rounded px-1 shrink-0">{f.productCode}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 truncate pl-[19px]">{f.companyName}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-500 tabular-nums">{f.debitAccount}</td>
                          <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{f.count}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-800 tabular-nums">{money(f.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-slate-700" colSpan={2}>
                          รวม {preview.files.length} ไฟล์
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700 tabular-nums">
                          {preview.files.reduce((n, f) => n + f.count, 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">
                          {money(preview.grandTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {preview.files.length > 0 && (
                <p className="text-[11.5px] text-slate-400 mt-2.5 leading-relaxed">
                  1 รายการค่าใช้จ่าย = 1 รายการโอน · อ้างอิงเป็น <code className="text-slate-500">E&lt;id&gt;</code> ·
                  หมายเหตุใช้รายละเอียดค่าใช้จ่าย ตัดที่ {EXPENSE_REMARK_MAX} ตัวอักษร ·
                  ดาวน์โหลดแล้ว<span className="font-medium text-slate-500">ยังไม่</span>เปลี่ยนสถานะเป็นจ่ายแล้ว
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose}
            className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-100">
            ยกเลิก
          </button>
          <button onClick={download} disabled={busy || loading || pastDate || !preview.files.length}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={15} />}
            ดาวน์โหลด {preview.files.length || ''} ไฟล์ (.zip)
          </button>
        </div>
      </div>
    </div>
  )
}
